import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Check, PawPrint, User, Camera } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivatePage() {
  const navigate = useNavigate();
  const { tagId } = useParams(); // Get tag ID from URL if present
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [createdPetId, setCreatedPetId] = useState(null);
  const [tagCode, setTagCode] = useState(tagId || '');

  // Check if tag is already activated
  useEffect(() => {
    const checkExistingTag = async () => {
      if (tagId) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/tags/${tagId}/check`);
          if (response.data.activated && response.data.pet_id) {
            // Tag already activated - redirect to pet profile
            navigate(`/pet/${response.data.pet_id}`, { replace: true });
          }
        } catch (error) {
          // Tag not found or network issue — continue with activation form.
          // We deliberately don't surface this to the user: a missing tag is
          // expected for new/unregistered codes.
        }
      }
    };
    checkExistingTag();
  }, [tagId, navigate]);

  const [formData, setFormData] = useState({
    // Owner info
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    backup_phone: '',
    password: '',
    // Pet info
    pet_name: '',
    pet_type: 'dog',
    breed: '',
    color: '',
    age: '',
    medical_notes: '',
    behaviour_notes: ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create pet profile with tag ID if present
      const payload = {
        ...formData,
        tag_id: tagCode || tagId || null
      };

      const response = await axios.post(`${BACKEND_URL}/api/pets/activate`, payload, {
        withCredentials: true
      });

      const petId = response.data.pet_id;
      setCreatedPetId(petId);

      // Upload photo if provided. Photo upload is non-blocking — the pet
      // profile has already been saved, so we don't want a photo failure
      // to make the user think activation failed.
      let photoUploadFailed = false;
      if (photoFile) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', photoFile);
          await axios.post(`${BACKEND_URL}/api/pets/${petId}/photo`, photoFormData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch {
          photoUploadFailed = true;
        }
      }

      setStep(3); // Success step
      if (photoUploadFailed) {
        toast.warning(
          "Pet profile created, but the photo couldn't be uploaded. You can add it from your Dashboard."
        );
      } else {
        toast.success('Pet profile created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pet profile');
    } finally {
      setLoading(false);
    }
  };

  const petTypes = [
    { value: 'dog', label: 'Dog' },
    { value: 'cat', label: 'Cat' },
    { value: 'bird', label: 'Bird' },
    { value: 'rabbit', label: 'Rabbit' },
    { value: 'hamster', label: 'Hamster' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main 
        className="pt-24 pb-20 relative"
        data-testid="activate-page"
      >
        {/* Background: subtle radial orange glow on solid dark */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(239,106,31,0.10) 0%, transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>
                <User className="w-4 h-4" />
              </div>
              <span className="hidden sm:inline text-sm font-medium">Owner Info</span>
            </div>
            <div className="w-8 h-0.5 bg-border" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>
                <PawPrint className="w-4 h-4" />
              </div>
              <span className="hidden sm:inline text-sm font-medium">Pet Info</span>
            </div>
            <div className="w-8 h-0.5 bg-border" />
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary text-white' : 'bg-muted'}`}>
                <Check className="w-4 h-4" />
              </div>
              <span className="hidden sm:inline text-sm font-medium">Complete</span>
            </div>
          </div>

          <div className="bg-card/95 backdrop-blur-sm rounded-2xl border border-border shadow-xl p-8">
            {step === 1 && (
              <>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground text-center mb-2">
                  Activate Your SaferPaws Tag
                </h1>
                <p className="text-muted-foreground text-center mb-8">
                  Let&apos;s start with your contact information
                </p>

                <form 
                  onSubmit={(e) => { e.preventDefault(); setStep(2); }}
                  className="space-y-6"
                >
                  <div>
                    <Label htmlFor="owner_name">Your Name *</Label>
                    <Input
                      id="owner_name"
                      value={formData.owner_name}
                      onChange={(e) => handleChange('owner_name', e.target.value)}
                      placeholder="John Doe"
                      required
                      className="mt-1"
                      data-testid="owner-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner_phone">Phone Number *</Label>
                    <Input
                      id="owner_phone"
                      type="tel"
                      value={formData.owner_phone}
                      onChange={(e) => handleChange('owner_phone', e.target.value)}
                      placeholder="+44 7700 900123"
                      required
                      className="mt-1"
                      data-testid="owner-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner_email">Email Address *</Label>
                    <Input
                      id="owner_email"
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) => handleChange('owner_email', e.target.value)}
                      placeholder="john@example.co.uk"
                      required
                      className="mt-1"
                      data-testid="owner-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="backup_phone">Backup Phone (Optional)</Label>
                    <Input
                      id="backup_phone"
                      type="tel"
                      value={formData.backup_phone}
                      onChange={(e) => handleChange('backup_phone', e.target.value)}
                      placeholder="+44 7700 900456"
                      className="mt-1"
                      data-testid="backup-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Create Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Min 6 characters"
                      required
                      minLength={6}
                      className="mt-1"
                      data-testid="password-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      You&apos;ll use this to log in and manage your pet&apos;s profile
                    </p>
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="continue-to-pet-btn"
                  >
                    Continue to Pet Information
                  </Button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground text-center mb-2">
                  Tell Us About Your Pet
                </h1>
                <p className="text-muted-foreground text-center mb-8">
                  This information helps reunite you with your pet
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Photo Upload */}
                  <div className="flex flex-col items-center">
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-32 h-32 rounded-full bg-secondary border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary transition-colors overflow-hidden"
                      data-testid="photo-upload-area"
                    >
                      {photoPreview ? (
                        <img src={photoPreview} alt="Pet preview" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <p className="mt-2 text-sm text-muted-foreground">
                      Click to upload pet photo
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="pet_name">Pet Name *</Label>
                    <Input
                      id="pet_name"
                      value={formData.pet_name}
                      onChange={(e) => handleChange('pet_name', e.target.value)}
                      placeholder="Bella"
                      required
                      className="mt-1"
                      data-testid="pet-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pet_type">Pet Type *</Label>
                      <Select 
                        value={formData.pet_type}
                        onValueChange={(value) => handleChange('pet_type', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="pet-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {petTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="breed">Breed</Label>
                      <Input
                        id="breed"
                        value={formData.breed}
                        onChange={(e) => handleChange('breed', e.target.value)}
                        placeholder="Golden Retriever"
                        className="mt-1"
                        data-testid="pet-breed-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="color">Color</Label>
                      <Input
                        id="color"
                        value={formData.color}
                        onChange={(e) => handleChange('color', e.target.value)}
                        placeholder="Golden"
                        className="mt-1"
                        data-testid="pet-color-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="age">Age</Label>
                      <Input
                        id="age"
                        value={formData.age}
                        onChange={(e) => handleChange('age', e.target.value)}
                        placeholder="3 years"
                        className="mt-1"
                        data-testid="pet-age-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="medical_notes">Medical Notes (Optional)</Label>
                    <Textarea
                      id="medical_notes"
                      value={formData.medical_notes}
                      onChange={(e) => handleChange('medical_notes', e.target.value)}
                      placeholder="Any medical conditions, allergies, or medications..."
                      className="mt-1"
                      rows={3}
                      data-testid="medical-notes-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="behaviour_notes">Behaviour Notes (Optional)</Label>
                    <Textarea
                      id="behaviour_notes"
                      value={formData.behaviour_notes}
                      onChange={(e) => handleChange('behaviour_notes', e.target.value)}
                      placeholder="Friendly, shy, or any tips for handling..."
                      className="mt-1"
                      rows={3}
                      data-testid="behaviour-notes-input"
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      className="flex-1 bg-primary hover:bg-primary/90"
                      disabled={loading}
                      data-testid="create-profile-btn"
                    >
                      {loading ? 'Creating Profile...' : 'Create Pet Profile'}
                    </Button>
                  </div>
                </form>
              </>
            )}

            {step === 3 && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-primary/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-primary" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                  Profile Created Successfully!
                </h1>
                <p className="mt-4 text-muted-foreground">
                  {tagCode ? `Tag ${tagCode} is now linked to ${formData.pet_name}'s profile.` : `${formData.pet_name}'s profile is now live.`}
                </p>
                
                {tagCode && (
                  <div className="mt-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <p className="text-sm text-foreground">
                      <strong className="text-primary">You&apos;re all set.</strong> Next time this tag is scanned, it will go directly to {formData.pet_name}&apos;s profile.
                    </p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-muted/50 border border-border rounded-lg text-left">
                  <p className="text-sm font-semibold text-primary mb-3">Your Login Details:</p>
                  <div className="space-y-2 bg-card rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Email:</span>
                      <span className="text-sm font-medium text-foreground">{formData.owner_email}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Password:</span>
                      <span className="text-sm font-medium text-foreground">••••••••</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Use these details to log in and edit {formData.pet_name}&apos;s profile anytime.
                  </p>
                </div>

                <div className="mt-8 space-y-4">
                  <Button 
                    onClick={() => navigate(`/pet/${createdPetId}`)}
                    className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                    data-testid="view-profile-btn"
                  >
                    View {formData.pet_name}&apos;s Profile
                  </Button>
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                    className="w-full rounded-full border-primary/40 text-primary hover:bg-primary/10"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
